import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import GeneralSelect from '../../../main/components/select/GeneralSelect';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import './HotelPricing.css';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import GeneralTable from '../../../main/components/grid/GeneralTable';
import GeneralModal from '../../../main/components/modal/general/GeneralModal';
import GeneralInput from '../../../main/components/generalInput/GeneralInput';
import { useTranslation } from 'react-i18next';

type TabKey = 'season' | 'ratePlan' | 'roomRate' | 'package';

type PaginationType = {
  page: number;
  itemPerPage: number;
  count: number;
  itemsPerPage: number[];
};

type GridItem = {
  entity: string;
  label: string;
  isShown: boolean;
  sort?: string;
  filter?: {
    value?: string;
    operator?: string;
  };
};

type HotelDto = {
  id: number;
  name: string;
};

type RoomTypeDto = {
  id: number;
  hotelId: number;
  name: string;
};

type SeasonPeriodDto = {
  id: number;
  hotelId: number;
  hotelName?: string;
  name: string;
  startDate: string;
  endDate: string;
  priority: number;
};

type RatePlanDto = {
  id: number;
  hotelId: number;
  hotelName?: string;
  name: string;
  code: string;
  isRefundable: boolean;
  mealPlanType: number;
};

type RoomRateDto = {
  id: number;
  roomTypeId: number;
  ratePlanId: number;
  seasonPeriodId: number;
  roomTypeName?: string;
  ratePlanName?: string;
  seasonPeriodName?: string;
  hotelId: number;
  pricePerNight: number;
  extraAdultPrice: number;
  childPrice: number;
  singleUsePrice: number;
  minStay: number;
  maxStay?: number | null;
};

type HotelPackageDto = {
  id: number;
  hotelId: number;
  name: string;
  code: string;
  defaultPrice: number;
  isPerNight: boolean;
  isPerPerson: boolean;
};

type SeasonPeriodForm = {
  id: number;
  hotelId: string;
  name: string;
  startDate: string;
  endDate: string;
  priority: number;
};

type RatePlanForm = {
  id: number;
  hotelId: string;
  name: string;
  code: string;
  mealPlanType: string;
  isRefundable: boolean;
};

type RoomRateForm = {
  id: number;
  hotelId: string;
  roomTypeId: string;
  ratePlanId: string;
  seasonPeriodId: string;
  pricePerNight: number | string;
  extraAdultPrice: number | string;
  childPrice: number | string;
  singleUsePrice: number | string;
  minStay: number | string;
  maxStay: number | string | null;
};

type HotelPackageForm = {
  id: number;
  hotelId: string;
  name: string;
  code: string;
  defaultPrice: number | string;
  isPerNight: boolean;
  isPerPerson: boolean;
};

const defaultPagination: PaginationType = {
  page: 1,
  itemPerPage: 10,
  count: 0,
  itemsPerPage: [10, 20, 50, 100],
};

const gridEntityMap = {
  seasonPeriod: 9,
  ratePlan: 10,
  roomRate: 11,
  hotelPackage: 12,
};

const HotelPricing: React.FC = () => {
  const { i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('season');

  const [hotels, setHotels] = useState<HotelDto[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomTypeDto[]>([]);

  const [seasonPeriods, setSeasonPeriods] = useState<SeasonPeriodDto[]>([]);
  const [ratePlans, setRatePlans] = useState<RatePlanDto[]>([]);
  const [roomRates, setRoomRates] = useState<RoomRateDto[]>([]);
  const [hotelPackages, setHotelPackages] = useState<HotelPackageDto[]>([]);

  const [seasonPagination, setSeasonPagination] = useState<PaginationType>(defaultPagination);
  const [ratePlanPagination, setRatePlanPagination] = useState<PaginationType>(defaultPagination);
  const [roomRatePagination, setRoomRatePagination] = useState<PaginationType>(defaultPagination);
  const [hotelPackagePagination, setHotelPackagePagination] = useState<PaginationType>(defaultPagination);

  const mealPlanOptions = useMemo(
    () => [
      { id: 1, name: i18n.t('hotelPricing.mealPlans.roomOnly') },
      { id: 2, name: i18n.t('hotelPricing.mealPlans.breakfast') },
      { id: 3, name: i18n.t('hotelPricing.mealPlans.halfBoard') },
      { id: 4, name: i18n.t('hotelPricing.mealPlans.fullBoard') },
      { id: 5, name: i18n.t('hotelPricing.mealPlans.allInclusive') },
    ],
    [i18n],
  );

  const seasonColumns = useMemo<GridItem[]>(
    () => [
      { entity: 'Hotel.Name', label: 'Hotel', isShown: true },
      { entity: 'Name', label: 'Name', isShown: true },
      { entity: 'StartDate', label: 'startDate', isShown: true },
      { entity: 'EndDate', label: 'endDate', isShown: true },
      { entity: 'Priority', label: 'priority', isShown: true },
    ],
    [],
  );

  const ratePlanColumns = useMemo<GridItem[]>(
    () => [
      { entity: 'Hotel.Name', label: 'Hotel', isShown: true },
      { entity: 'Name', label: 'Name', isShown: true },
      { entity: 'Code', label: 'Code', isShown: true },
      { entity: 'IsRefundable', label: 'isRefundable', isShown: true },
      { entity: 'MealPlanType', label: 'mealPlanType', isShown: true },
    ],
    [],
  );

  const roomRateColumns = useMemo<GridItem[]>(
    () => [
      { entity: 'RoomType.Hotel.Name', label: 'Hotel', isShown: true },
      { entity: 'RoomType.Name', label: 'roomType', isShown: true },
      { entity: 'RatePlan.Name', label: 'ratePlan', isShown: true },
      { entity: 'SeasonPeriod.Name', label: 'seasonPeriod', isShown: true },
      { entity: 'PricePerNight', label: 'pricePerNight', isShown: true },
      { entity: 'MinStay', label: 'minStay', isShown: true },
      { entity: 'MaxStay', label: 'maxStay', isShown: true },
    ],
    [],
  );

  const hotelPackageColumns = useMemo<GridItem[]>(
    () => [
      { entity: 'Hotel.Name', label: 'Hotel', isShown: true },
      { entity: 'Name', label: 'Name', isShown: true },
      { entity: 'Code', label: 'Code', isShown: true },
      { entity: 'DefaultPrice', label: 'defaultPrice', isShown: true },
      { entity: 'IsPerNight', label: 'isPerNight', isShown: true },
      { entity: 'IsPerPerson', label: 'isPerPerson', isShown: true },
    ],
    [],
  );

  const [seasonGridItems] = useState<GridItem[]>([]);
  const [ratePlanGridItems] = useState<GridItem[]>([]);
  const [roomRateGridItems] = useState<GridItem[]>([]);
  const [hotelPackageGridItems] = useState<GridItem[]>([]);

  const [seasonModalOpen, setSeasonModalOpen] = useState(false);
  const [ratePlanModalOpen, setRatePlanModalOpen] = useState(false);
  const [roomRateModalOpen, setRoomRateModalOpen] = useState(false);
  const [hotelPackageModalOpen, setHotelPackageModalOpen] = useState(false);

  const [selectedSeason, setSelectedSeason] = useState<SeasonPeriodDto | null>(null);
  const [selectedRatePlan, setSelectedRatePlan] = useState<RatePlanDto | null>(null);
  const [selectedRoomRate, setSelectedRoomRate] = useState<RoomRateDto | null>(null);
  const [selectedHotelPackage, setSelectedHotelPackage] = useState<HotelPackageDto | null>(null);
  const [selectedHotelFilterId, setSelectedHotelFilterId] = useState<string>('');

  const seasonForm = useForm<SeasonPeriodForm>({
    defaultValues: {
      id: 0,
      hotelId: '',
      name: '',
      startDate: '',
      endDate: '',
      priority: 1,
    },
  });

  const ratePlanForm = useForm<RatePlanForm>({
    defaultValues: {
      id: 0,
      hotelId: '',
      name: '',
      code: '',
      mealPlanType: '1',
      isRefundable: false,
    },
  });

  const roomRateForm = useForm<RoomRateForm>({
    defaultValues: {
      id: 0,
      hotelId: '',
      roomTypeId: '',
      ratePlanId: '',
      seasonPeriodId: '',
      pricePerNight: 0,
      extraAdultPrice: 0,
      childPrice: 0,
      singleUsePrice: 0,
      minStay: 1,
      maxStay: null,
    },
  });

  const hotelPackageForm = useForm<HotelPackageForm>({
    defaultValues: {
      id: 0,
      hotelId: '',
      name: '',
      code: '',
      defaultPrice: 0,
      isPerNight: false,
      isPerPerson: false,
    },
  });

  const selectedRoomRateHotelId = roomRateForm.watch('hotelId');

  const filteredRoomTypes = useMemo(() => {
    if (!selectedRoomRateHotelId) return [];
    return roomTypes.filter((x) => String(x.hotelId) === String(selectedRoomRateHotelId));
  }, [roomTypes, selectedRoomRateHotelId]);

  const filteredRatePlans = useMemo(() => {
    if (!selectedRoomRateHotelId) return [];
    return ratePlans.filter((x) => String(x.hotelId) === String(selectedRoomRateHotelId));
  }, [ratePlans, selectedRoomRateHotelId]);

  const filteredSeasonPeriods = useMemo(() => {
    if (!selectedRoomRateHotelId) return [];
    return seasonPeriods.filter((x) => String(x.hotelId) === String(selectedRoomRateHotelId));
  }, [seasonPeriods, selectedRoomRateHotelId]);

  const hotelNameById = useMemo(() => {
    return hotels.reduce<Record<number, string>>((acc, hotel) => {
      acc[hotel.id] = hotel.name;
      return acc;
    }, {});
  }, [hotels]);

  const seasonTableData = useMemo(
    () =>
      seasonPeriods.map((item) => ({
        ...item,
        Hotel: {
          Name: item.hotelName ?? hotelNameById[item.hotelId] ?? '',
        },
        Name: item.name,
        StartDate: item.startDate,
        EndDate: item.endDate,
        Priority: item.priority,
      })),
    [hotelNameById, seasonPeriods],
  );

  const ratePlanTableData = useMemo(
    () =>
      ratePlans.map((item) => ({
        ...item,
        Hotel: {
          Name: item.hotelName ?? hotelNameById[item.hotelId] ?? '',
        },
        Name: item.name,
        Code: item.code,
        IsRefundable: item.isRefundable,
        MealPlanType: mealPlanOptions.find((option) => option.id === Number(item.mealPlanType))?.name ?? String(item.mealPlanType),
      })),
    [hotelNameById, mealPlanOptions, ratePlans],
  );

  const roomRateTableData = useMemo(
    () =>
      roomRates.map((item) => ({
        ...item,
        RoomType: {
          Name: item.roomTypeName ?? '',
          Hotel: {
            Name: hotelNameById[item.hotelId] ?? '',
          },
        },
        RatePlan: {
          Name: item.ratePlanName ?? '',
        },
        SeasonPeriod: {
          Name: item.seasonPeriodName ?? '',
        },
        PricePerNight: item.pricePerNight,
        MinStay: item.minStay,
        MaxStay: item.maxStay,
      })),
    [hotelNameById, roomRates],
  );

  const hotelPackageTableData = useMemo(
    () =>
      hotelPackages.map((item) => ({
        ...item,
        Hotel: {
          Name: hotelNameById[item.hotelId] ?? '',
        },
        Name: item.name,
        Code: item.code,
        DefaultPrice: item.defaultPrice,
        IsPerNight: item.isPerNight ? i18n.t('generic.yes', { defaultValue: 'Yes' }) : i18n.t('generic.no', { defaultValue: 'No' }),
        IsPerPerson: item.isPerPerson ? i18n.t('generic.yes', { defaultValue: 'Yes' }) : i18n.t('generic.no', { defaultValue: 'No' }),
      })),
    [hotelNameById, hotelPackages, i18n],
  );

  const pagedHotelPackageTableData = useMemo(() => {
    const start = (hotelPackagePagination.page - 1) * hotelPackagePagination.itemPerPage;
    const end = start + hotelPackagePagination.itemPerPage;
    return hotelPackageTableData.slice(start, end);
  }, [hotelPackagePagination.itemPerPage, hotelPackagePagination.page, hotelPackageTableData]);

  const loadLookups = useCallback(async () => {
    try {
      const [hotelsRes, roomTypesRes] = await Promise.all([
        axiosInstance.get(ApiConstants.hotel.hotels),
        axiosInstance.get(ApiConstants.hotel.roomTypes),
      ]);

      setHotels(hotelsRes.data || []);
      setRoomTypes(roomTypesRes.data || []);
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.loadLookupsError'));
    }
  }, [i18n]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    setSeasonPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    setRatePlanPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    setRoomRatePagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    setHotelPackagePagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [selectedHotelFilterId]);

  const loadSeasonPeriods = useCallback(
    async (pg: PaginationType) => {
      try {
        const request = {
          gridItems: buildGridRequest(
            seasonGridItems,
            selectedHotelFilterId ? { entity: 'HotelId', value: selectedHotelFilterId } : null,
            seasonColumns,
          ),
          pagination: pg,
        };

        const res = await axiosInstance.post(ApiConstants.hotelPricing.seasonPeriodList, request);
        setSeasonPeriods(res.data.items || []);
        setSeasonPagination((prev) => {
          const next = normalizePagination(res.data.pagination, pg);
          return isSamePagination(prev, next) ? prev : next;
        });
      } catch (error: any) {
        toast.error(error?.response?.data || i18n.t('hotelPricing.messages.loadSeasonPeriodsError'));
      }
    },
    [i18n, seasonColumns, seasonGridItems, selectedHotelFilterId],
  );

  const loadRatePlans = useCallback(
    async (pg: PaginationType) => {
      try {
        const request = {
          gridItems: buildGridRequest(
            ratePlanGridItems,
            selectedHotelFilterId ? { entity: 'HotelId', value: selectedHotelFilterId } : null,
            ratePlanColumns,
          ),
          pagination: pg,
        };

        const res = await axiosInstance.post(ApiConstants.hotelPricing.ratePlanList, request);
        setRatePlans(res.data.items || []);
        setRatePlanPagination((prev) => {
          const next = normalizePagination(res.data.pagination, pg);
          return isSamePagination(prev, next) ? prev : next;
        });
      } catch (error: any) {
        toast.error(error?.response?.data || i18n.t('hotelPricing.messages.loadRatePlansError'));
      }
    },
    [i18n, ratePlanColumns, ratePlanGridItems, selectedHotelFilterId],
  );

  const loadRoomRates = useCallback(
    async (pg: PaginationType) => {
      try {
        const request = {
          gridItems: buildGridRequest(
            roomRateGridItems,
            selectedHotelFilterId ? { entity: 'RoomType.HotelId', value: selectedHotelFilterId } : null,
            roomRateColumns,
          ),
          pagination: pg,
        };

        const res = await axiosInstance.post(ApiConstants.hotelPricing.roomRateList, request);
        setRoomRates(res.data.items || []);
        setRoomRatePagination((prev) => {
          const next = normalizePagination(res.data.pagination, pg);
          return isSamePagination(prev, next) ? prev : next;
        });
      } catch (error: any) {
        toast.error(error?.response?.data || i18n.t('hotelPricing.messages.loadRoomRatesError'));
      }
    },
    [i18n, roomRateColumns, roomRateGridItems, selectedHotelFilterId],
  );

  const loadHotelPackages = useCallback(async () => {
    if (hotels.length === 0) {
      setHotelPackages([]);
      setHotelPackagePagination((prev) => ({ ...prev, count: 0 }));
      return;
    }

    try {
      const hotelIds =
        selectedHotelFilterId && Number(selectedHotelFilterId) > 0
          ? [Number(selectedHotelFilterId)]
          : hotels.map((hotel) => hotel.id).filter((id) => id > 0);

      if (hotelIds.length === 0) return;

      const responses = await Promise.all(
        hotelIds.map((hotelId) =>
          axiosInstance.get(ApiConstants.hotel.packages, {
            params: { hotelId },
          }),
        ),
      );

      const merged = responses.flatMap((res) => (Array.isArray(res.data) ? res.data : []));
      setHotelPackages(merged);
      setHotelPackagePagination((prev) => ({
        ...prev,
        count: merged.length,
      }));
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.loadHotelPackagesError'));
    }
  }, [hotels, i18n, selectedHotelFilterId]);

  useEffect(() => {
    loadSeasonPeriods({
      ...seasonPagination,
      page: seasonPagination.page,
      itemPerPage: seasonPagination.itemPerPage,
    });
  }, [loadSeasonPeriods, seasonPagination.page, seasonPagination.itemPerPage]);

  useEffect(() => {
    loadRatePlans({
      ...ratePlanPagination,
      page: ratePlanPagination.page,
      itemPerPage: ratePlanPagination.itemPerPage,
    });
  }, [loadRatePlans, ratePlanPagination.page, ratePlanPagination.itemPerPage]);

  useEffect(() => {
    loadRoomRates({
      ...roomRatePagination,
      page: roomRatePagination.page,
      itemPerPage: roomRatePagination.itemPerPage,
    });
  }, [loadRoomRates, roomRatePagination.page, roomRatePagination.itemPerPage]);

  useEffect(() => {
    loadHotelPackages();
  }, [loadHotelPackages]);

  const openCreateSeasonModal = () => {
    setSelectedSeason(null);
    seasonForm.reset({
      id: 0,
      hotelId: selectedHotelFilterId,
      name: '',
      startDate: '',
      endDate: '',
      priority: 1,
    });
    setSeasonModalOpen(true);
  };

  const openEditSeasonModal = (item: SeasonPeriodDto) => {
    setSelectedSeason(item);
    seasonForm.reset({
      id: item.id,
      hotelId: String(item.hotelId),
      name: item.name,
      startDate: toDateInput(item.startDate),
      endDate: toDateInput(item.endDate),
      priority: item.priority,
    });
    setSeasonModalOpen(true);
  };

  const openCreateRatePlanModal = () => {
    setSelectedRatePlan(null);
    ratePlanForm.reset({
      id: 0,
      hotelId: selectedHotelFilterId,
      name: '',
      code: '',
      mealPlanType: '1',
      isRefundable: false,
    });
    setRatePlanModalOpen(true);
  };

  const openEditRatePlanModal = (item: RatePlanDto) => {
    setSelectedRatePlan(item);
    ratePlanForm.reset({
      id: item.id,
      hotelId: String(item.hotelId),
      name: item.name,
      code: item.code,
      mealPlanType: String(item.mealPlanType),
      isRefundable: item.isRefundable,
    });
    setRatePlanModalOpen(true);
  };

  const openCreateRoomRateModal = () => {
    setSelectedRoomRate(null);
    roomRateForm.reset({
      id: 0,
      hotelId: selectedHotelFilterId,
      roomTypeId: '',
      ratePlanId: '',
      seasonPeriodId: '',
      pricePerNight: 0,
      extraAdultPrice: 0,
      childPrice: 0,
      singleUsePrice: 0,
      minStay: 1,
      maxStay: null,
    });
    setRoomRateModalOpen(true);
  };

  const openEditRoomRateModal = (item: RoomRateDto) => {
    setSelectedRoomRate(item);
    roomRateForm.reset({
      id: item.id,
      hotelId: String(item.hotelId),
      roomTypeId: String(item.roomTypeId),
      ratePlanId: String(item.ratePlanId),
      seasonPeriodId: String(item.seasonPeriodId),
      pricePerNight: item.pricePerNight,
      extraAdultPrice: item.extraAdultPrice,
      childPrice: item.childPrice,
      singleUsePrice: item.singleUsePrice,
      minStay: item.minStay,
      maxStay: item.maxStay ?? null,
    });
    setRoomRateModalOpen(true);
  };

  const openCreateHotelPackageModal = () => {
    setSelectedHotelPackage(null);
    hotelPackageForm.reset({
      id: 0,
      hotelId: selectedHotelFilterId,
      name: '',
      code: '',
      defaultPrice: 0,
      isPerNight: false,
      isPerPerson: false,
    });
    setHotelPackageModalOpen(true);
  };

  const openEditHotelPackageModal = (item: HotelPackageDto) => {
    setSelectedHotelPackage(item);
    hotelPackageForm.reset({
      id: item.id,
      hotelId: String(item.hotelId),
      name: item.name,
      code: item.code,
      defaultPrice: item.defaultPrice,
      isPerNight: item.isPerNight,
      isPerPerson: item.isPerPerson,
    });
    setHotelPackageModalOpen(true);
  };

  const onSaveSeason = seasonForm.handleSubmit(async (formData) => {
    try {
      await axiosInstance.post(ApiConstants.hotelPricing.seasonPeriodSave, {
        id: Number(formData.id || 0),
        hotelId: Number(formData.hotelId),
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        priority: Number(formData.priority),
      });

      toast.success(i18n.t(selectedSeason ? 'hotelPricing.messages.seasonUpdated' : 'hotelPricing.messages.seasonCreated'));
      setSeasonModalOpen(false);
      await loadLookups();
      loadSeasonPeriods(seasonPagination);
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.saveSeasonError'));
    }
  });

  const onSaveRatePlan = ratePlanForm.handleSubmit(async (formData) => {
    try {
      await axiosInstance.post(ApiConstants.hotelPricing.ratePlanSave, {
        id: Number(formData.id || 0),
        hotelId: Number(formData.hotelId),
        name: formData.name,
        code: formData.code,
        isRefundable: formData.isRefundable,
        mealPlanType: Number(formData.mealPlanType),
      });

      toast.success(i18n.t(selectedRatePlan ? 'hotelPricing.messages.ratePlanUpdated' : 'hotelPricing.messages.ratePlanCreated'));
      setRatePlanModalOpen(false);
      await loadLookups();
      loadRatePlans(ratePlanPagination);
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.saveRatePlanError'));
    }
  });

  const onSaveRoomRate = roomRateForm.handleSubmit(async (formData) => {
    try {
      await axiosInstance.post(ApiConstants.hotelPricing.roomRateSave, {
        id: Number(formData.id || 0),
        roomTypeId: Number(formData.roomTypeId),
        ratePlanId: Number(formData.ratePlanId),
        seasonPeriodId: Number(formData.seasonPeriodId),
        pricePerNight: Number(formData.pricePerNight),
        extraAdultPrice: Number(formData.extraAdultPrice),
        childPrice: Number(formData.childPrice),
        singleUsePrice: Number(formData.singleUsePrice),
        minStay: Number(formData.minStay),
        maxStay: formData.maxStay === '' || formData.maxStay === null || formData.maxStay === undefined ? null : Number(formData.maxStay),
      });

      toast.success(i18n.t(selectedRoomRate ? 'hotelPricing.messages.roomRateUpdated' : 'hotelPricing.messages.roomRateCreated'));
      setRoomRateModalOpen(false);
      await loadLookups();
      loadRoomRates(roomRatePagination);
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.saveRoomRateError'));
    }
  });

  const onSaveHotelPackage = hotelPackageForm.handleSubmit(async (formData) => {
    try {
      await axiosInstance.post(ApiConstants.hotel.packageSave, {
        id: Number(formData.id || 0),
        hotelId: Number(formData.hotelId),
        name: formData.name,
        code: formData.code,
        defaultPrice: Number(formData.defaultPrice),
        isPerNight: formData.isPerNight,
        isPerPerson: formData.isPerPerson,
      });

      toast.success(i18n.t(selectedHotelPackage ? 'hotelPricing.messages.hotelPackageUpdated' : 'hotelPricing.messages.hotelPackageCreated'));
      setHotelPackageModalOpen(false);
      await loadHotelPackages();
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.saveHotelPackageError'));
    }
  });

  const deleteHotelPricingItem = useCallback(async (url: string, id: number) => {
    if (!Number.isFinite(id) || id <= 0) {
      throw new Error('Invalid item id for delete.');
    }

    await axiosInstance.request({
      method: 'DELETE',
      url,
      data: id,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }, []);

  const onDeleteSeason = async (item: SeasonPeriodDto) => {
    if (!window.confirm(i18n.t('hotelPricing.confirmations.deleteSeason', { name: item.name }))) return;

    try {
      await deleteHotelPricingItem(ApiConstants.hotelPricing.seasonPeriodDelete, Number(item.id));
      toast.success(i18n.t('hotelPricing.messages.seasonDeleted'));
      await loadSeasonPeriods(seasonPagination);
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.deleteSeasonError'));
    }
  };

  const onDeleteRatePlan = async (item: RatePlanDto) => {
    if (!window.confirm(i18n.t('hotelPricing.confirmations.deleteRatePlan', { name: item.name }))) return;

    try {
      await deleteHotelPricingItem(ApiConstants.hotelPricing.ratePlanDelete, Number(item.id));
      toast.success(i18n.t('hotelPricing.messages.ratePlanDeleted'));
      await loadRatePlans(ratePlanPagination);
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.deleteRatePlanError'));
    }
  };

  const onDeleteRoomRate = async (item: RoomRateDto) => {
    if (
      !window.confirm(
        i18n.t('hotelPricing.confirmations.deleteRoomRate', {
          roomType: item.roomTypeName,
          ratePlan: item.ratePlanName,
        }),
      )
    )
      return;

    try {
      await deleteHotelPricingItem(ApiConstants.hotelPricing.roomRateDelete, Number(item.id));
      toast.success(i18n.t('hotelPricing.messages.roomRateDeleted'));
      await loadRoomRates(roomRatePagination);
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.deleteRoomRateError'));
    }
  };

  const onDeleteHotelPackage = async (item: HotelPackageDto) => {
    if (!window.confirm(i18n.t('hotelPricing.confirmations.deleteHotelPackage', { name: item.name }))) return;

    try {
      await axiosInstance.delete(ApiConstants.hotel.packageDelete, {
        params: { id: item.id },
      });
      toast.success(i18n.t('hotelPricing.messages.hotelPackageDeleted'));
      await loadHotelPackages();
    } catch (error: any) {
      toast.error(error?.response?.data || i18n.t('hotelPricing.messages.deleteHotelPackageError'));
    }
  };

  return (
    <BasicCli>
      <div className="hotelPricingPage">
        <div className="hotelPricingToolbar">
          <div>
            <h2 className="hotelPricingTitle">{i18n.t('hotelPricing.title')}</h2>
            <p className="hotelPricingSubtitle">{i18n.t('hotelPricing.subtitle')}</p>
          </div>

          <div className="hotelPricingActions">
            <div className="selectContainer" style={{ minWidth: 240 }}>
              <label htmlFor="hotelPricingFilter">{i18n.t('hotelPricing.hotelFilter')}</label>
              <select
                id="hotelPricingFilter"
                className="form-select"
                value={selectedHotelFilterId}
                onChange={(e) => setSelectedHotelFilterId(e.target.value)}
              >
                <option value="">{i18n.t('hotel.allHotels')}</option>
                {hotels.map((hotel) => (
                  <option key={hotel.id} value={hotel.id}>
                    {hotel.name}
                  </option>
                ))}
              </select>
            </div>
            {activeTab === 'season' && (
              <button className="hpPrimaryBtn" onClick={openCreateSeasonModal}>
                {i18n.t('hotelPricing.actions.addSeasonPeriod')}
              </button>
            )}

            {activeTab === 'ratePlan' && (
              <button className="hpPrimaryBtn" onClick={openCreateRatePlanModal}>
                {i18n.t('hotelPricing.actions.addRatePlan')}
              </button>
            )}

            {activeTab === 'roomRate' && (
              <button className="hpPrimaryBtn" onClick={openCreateRoomRateModal}>
                {i18n.t('hotelPricing.actions.addRoomRate')}
              </button>
            )}

            {activeTab === 'package' && (
              <button className="hpPrimaryBtn" onClick={openCreateHotelPackageModal}>
                {i18n.t('hotelPricing.actions.addHotelPackage')}
              </button>
            )}
          </div>
        </div>

        <div className="hotelPricingTabs">
          <button className={`hotelPricingTab ${activeTab === 'season' ? 'active' : ''}`} onClick={() => setActiveTab('season')}>
            {i18n.t('hotelPricing.tabs.seasonPeriods')}
          </button>
          <button className={`hotelPricingTab ${activeTab === 'ratePlan' ? 'active' : ''}`} onClick={() => setActiveTab('ratePlan')}>
            {i18n.t('hotelPricing.tabs.ratePlans')}
          </button>
          <button className={`hotelPricingTab ${activeTab === 'roomRate' ? 'active' : ''}`} onClick={() => setActiveTab('roomRate')}>
            {i18n.t('hotelPricing.tabs.roomRates')}
          </button>
          <button className={`hotelPricingTab ${activeTab === 'package' ? 'active' : ''}`} onClick={() => setActiveTab('package')}>
            {i18n.t('hotelPricing.tabs.hotelPackages')}
          </button>
        </div>

        <div className="hotelPricingCard">
          {activeTab === 'season' && (
            <GeneralTable
              data={seasonTableData}
              title={i18n.t('hotelPricing.tabs.seasonPeriods')}
              hasRp
              columns={seasonGridItems.length ? seasonGridItems : seasonColumns}
              gridEntity={gridEntityMap.seasonPeriod}
              pagination={seasonPagination}
              selectedItem={() => {}}
              selectedItemPagination={(pg: PaginationType) => setSeasonPagination({ ...seasonPagination, ...pg })}
              onEdit={openEditSeasonModal}
              onDelete={onDeleteSeason}
            />
          )}

          {activeTab === 'ratePlan' && (
            <GeneralTable
              data={ratePlanTableData}
              title={i18n.t('hotelPricing.tabs.ratePlans')}
              hasRp
              columns={ratePlanGridItems.length ? ratePlanGridItems : ratePlanColumns}
              gridEntity={gridEntityMap.ratePlan}
              pagination={ratePlanPagination}
              selectedItem={() => {}}
              selectedItemPagination={(pg: PaginationType) => setRatePlanPagination({ ...ratePlanPagination, ...pg })}
              onEdit={openEditRatePlanModal}
              onDelete={onDeleteRatePlan}
            />
          )}

          {activeTab === 'roomRate' && (
            <GeneralTable
              data={roomRateTableData}
              title={i18n.t('hotelPricing.tabs.roomRates')}
              hasRp
              columns={roomRateGridItems.length ? roomRateGridItems : roomRateColumns}
              gridEntity={gridEntityMap.roomRate}
              pagination={roomRatePagination}
              selectedItem={() => {}}
              selectedItemPagination={(pg: PaginationType) => setRoomRatePagination({ ...roomRatePagination, ...pg })}
              onEdit={openEditRoomRateModal}
              onDelete={onDeleteRoomRate}
            />
          )}

          {activeTab === 'package' && (
            <GeneralTable
              data={pagedHotelPackageTableData}
              title={i18n.t('hotelPricing.tabs.hotelPackages')}
              hasRp
              columns={hotelPackageGridItems.length ? hotelPackageGridItems : hotelPackageColumns}
              gridEntity={gridEntityMap.hotelPackage}
              pagination={hotelPackagePagination}
              selectedItem={() => {}}
              selectedItemPagination={(pg: PaginationType) =>
                setHotelPackagePagination((prev) => ({
                  ...prev,
                  ...pg,
                  count: hotelPackageTableData.length,
                }))
              }
              onEdit={openEditHotelPackageModal}
              onDelete={onDeleteHotelPackage}
            />
          )}
        </div>

        {seasonModalOpen && (
          <GeneralModal
            headerName={i18n.t(selectedSeason ? 'hotelPricing.modals.editSeasonTitle' : 'hotelPricing.modals.createSeasonTitle')}
            handleClose={() => setSeasonModalOpen(false)}
            hideFooter
            width="760px"
            radius="16px"
          >
            <form className="hotelPricingForm" onSubmit={onSaveSeason}>
              <div className="hotelPricingGrid twoCols">
                <GeneralSelect
                  label={i18n.t('hotel.hotel')}
                  name="hotelId"
                  control={seasonForm.control}
                  required
                  data={hotels}
                  value="name"
                  errors={seasonForm.formState.errors}
                  preSelectedVal={i18n.t('hotel.selectHotel')}
                />

                <GeneralInput
                  label={i18n.t('generic.name')}
                  name="name"
                  type="text"
                  placeholder={i18n.t('hotelPricing.placeholders.seasonName')}
                  control={seasonForm.control}
                  errors={seasonForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('generic.startDate')}
                  name="startDate"
                  type="date"
                  placeholder=""
                  control={seasonForm.control}
                  errors={seasonForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('generic.endDate')}
                  name="endDate"
                  type="date"
                  placeholder=""
                  control={seasonForm.control}
                  errors={seasonForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('hotelPricing.fields.priority')}
                  name="priority"
                  type="number"
                  placeholder="1"
                  control={seasonForm.control}
                  errors={seasonForm.formState.errors}
                  required
                />
              </div>

              <div className="hotelPricingModalFooter">
                <button type="button" className="hpSecondaryBtn" onClick={() => setSeasonModalOpen(false)}>
                  {i18n.t('generic.cancel')}
                </button>
                <button type="submit" className="hpPrimaryBtn">
                  {i18n.t(selectedSeason ? 'hotelPricing.actions.update' : 'hotelPricing.actions.create')}
                </button>
              </div>
            </form>
          </GeneralModal>
        )}

        {ratePlanModalOpen && (
          <GeneralModal
            headerName={i18n.t(selectedRatePlan ? 'hotelPricing.modals.editRatePlanTitle' : 'hotelPricing.modals.createRatePlanTitle')}
            handleClose={() => setRatePlanModalOpen(false)}
            hideFooter
            width="760px"
            radius="16px"
          >
            <form className="hotelPricingForm" onSubmit={onSaveRatePlan}>
              <div className="hotelPricingGrid twoCols">
                <GeneralSelect
                  label={i18n.t('hotel.hotel')}
                  name="hotelId"
                  control={ratePlanForm.control}
                  required
                  data={hotels}
                  value="name"
                  errors={ratePlanForm.formState.errors}
                  preSelectedVal={i18n.t('hotel.selectHotel')}
                />

                <GeneralInput
                  label={i18n.t('generic.name')}
                  name="name"
                  type="text"
                  placeholder={i18n.t('hotelPricing.placeholders.ratePlanName')}
                  control={ratePlanForm.control}
                  errors={ratePlanForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('generic.code')}
                  name="code"
                  type="text"
                  placeholder={i18n.t('hotelPricing.placeholders.ratePlanCode')}
                  control={ratePlanForm.control}
                  errors={ratePlanForm.formState.errors}
                  required
                />

                <GeneralSelect
                  label={i18n.t('hotelPricing.fields.mealPlan')}
                  name="mealPlanType"
                  control={ratePlanForm.control}
                  required
                  data={mealPlanOptions}
                  value="name"
                  errors={ratePlanForm.formState.errors}
                  preSelectedVal={i18n.t('hotelPricing.placeholders.selectMealPlan')}
                />
              </div>

              <div className="hotelPricingInlineSwitch">
                <label className="hpCheckboxRow">
                  <input
                    type="checkbox"
                    checked={!!ratePlanForm.watch('isRefundable')}
                    onChange={(e) => ratePlanForm.setValue('isRefundable', e.target.checked)}
                  />
                  <span>{i18n.t('hotelPricing.fields.refundable')}</span>
                </label>
              </div>

              <div className="hotelPricingModalFooter">
                <button type="button" className="hpSecondaryBtn" onClick={() => setRatePlanModalOpen(false)}>
                  {i18n.t('generic.cancel')}
                </button>
                <button type="submit" className="hpPrimaryBtn">
                  {i18n.t(selectedRatePlan ? 'hotelPricing.actions.update' : 'hotelPricing.actions.create')}
                </button>
              </div>
            </form>
          </GeneralModal>
        )}

        {roomRateModalOpen && (
          <GeneralModal
            headerName={i18n.t(selectedRoomRate ? 'hotelPricing.modals.editRoomRateTitle' : 'hotelPricing.modals.createRoomRateTitle')}
            handleClose={() => setRoomRateModalOpen(false)}
            hideFooter
            width="980px"
            radius="16px"
          >
            <form className="hotelPricingForm" onSubmit={onSaveRoomRate}>
              <div className="hotelPricingGrid threeCols">
                <GeneralSelect
                  label={i18n.t('hotel.hotel')}
                  name="hotelId"
                  control={roomRateForm.control}
                  required
                  data={hotels}
                  value="name"
                  errors={roomRateForm.formState.errors}
                  preSelectedVal={i18n.t('hotel.selectHotel')}
                  setSelectedVal={() => {
                    roomRateForm.setValue('roomTypeId', '');
                    roomRateForm.setValue('ratePlanId', '');
                    roomRateForm.setValue('seasonPeriodId', '');
                  }}
                />

                <GeneralSelect
                  label={i18n.t('reservationDesk.roomType')}
                  name="roomTypeId"
                  control={roomRateForm.control}
                  required
                  data={filteredRoomTypes}
                  value="name"
                  errors={roomRateForm.formState.errors}
                  preSelectedVal={i18n.t('hotelPricing.placeholders.selectRoomType')}
                />

                <GeneralSelect
                  label={i18n.t('hotelPricing.fields.ratePlan')}
                  name="ratePlanId"
                  control={roomRateForm.control}
                  required
                  data={filteredRatePlans}
                  value="name"
                  errors={roomRateForm.formState.errors}
                  preSelectedVal={i18n.t('hotelPricing.placeholders.selectRatePlan')}
                />

                <GeneralSelect
                  label={i18n.t('hotelPricing.fields.seasonPeriod')}
                  name="seasonPeriodId"
                  control={roomRateForm.control}
                  required
                  data={filteredSeasonPeriods}
                  value="name"
                  errors={roomRateForm.formState.errors}
                  preSelectedVal={i18n.t('hotelPricing.placeholders.selectSeasonPeriod')}
                />

                <GeneralInput
                  label={i18n.t('hotelPricing.fields.pricePerNight')}
                  name="pricePerNight"
                  type="number"
                  placeholder="0"
                  control={roomRateForm.control}
                  errors={roomRateForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('hotelPricing.fields.extraAdultPrice')}
                  name="extraAdultPrice"
                  type="number"
                  placeholder="0"
                  control={roomRateForm.control}
                  errors={roomRateForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('hotelPricing.fields.childPrice')}
                  name="childPrice"
                  type="number"
                  placeholder="0"
                  control={roomRateForm.control}
                  errors={roomRateForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('hotelPricing.fields.singleUsePrice')}
                  name="singleUsePrice"
                  type="number"
                  placeholder="0"
                  control={roomRateForm.control}
                  errors={roomRateForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('hotelPricing.fields.minStay')}
                  name="minStay"
                  type="number"
                  placeholder="1"
                  control={roomRateForm.control}
                  errors={roomRateForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('hotelPricing.fields.maxStay')}
                  name="maxStay"
                  type="number"
                  placeholder={i18n.t('hotelPricing.placeholders.optional')}
                  control={roomRateForm.control}
                  errors={roomRateForm.formState.errors}
                />
              </div>

              <div className="hotelPricingModalFooter">
                <button type="button" className="hpSecondaryBtn" onClick={() => setRoomRateModalOpen(false)}>
                  {i18n.t('generic.cancel')}
                </button>
                <button type="submit" className="hpPrimaryBtn">
                  {i18n.t(selectedRoomRate ? 'hotelPricing.actions.update' : 'hotelPricing.actions.create')}
                </button>
              </div>
            </form>
          </GeneralModal>
        )}

        {hotelPackageModalOpen && (
          <GeneralModal
            headerName={i18n.t(selectedHotelPackage ? 'hotelPricing.modals.editHotelPackageTitle' : 'hotelPricing.modals.createHotelPackageTitle')}
            handleClose={() => setHotelPackageModalOpen(false)}
            hideFooter
            width="760px"
            radius="16px"
          >
            <form className="hotelPricingForm" onSubmit={onSaveHotelPackage}>
              <div className="hotelPricingGrid twoCols">
                <GeneralSelect
                  label={i18n.t('hotel.hotel')}
                  name="hotelId"
                  control={hotelPackageForm.control}
                  required
                  data={hotels}
                  value="name"
                  errors={hotelPackageForm.formState.errors}
                  preSelectedVal={i18n.t('hotel.selectHotel')}
                />

                <GeneralInput
                  label={i18n.t('generic.name')}
                  name="name"
                  type="text"
                  placeholder={i18n.t('hotelPricing.placeholders.hotelPackageName')}
                  control={hotelPackageForm.control}
                  errors={hotelPackageForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('generic.code')}
                  name="code"
                  type="text"
                  placeholder={i18n.t('hotelPricing.placeholders.hotelPackageCode')}
                  control={hotelPackageForm.control}
                  errors={hotelPackageForm.formState.errors}
                  required
                />

                <GeneralInput
                  label={i18n.t('hotelPricing.fields.defaultPrice')}
                  name="defaultPrice"
                  type="number"
                  placeholder="0"
                  control={hotelPackageForm.control}
                  errors={hotelPackageForm.formState.errors}
                  required
                />
              </div>

              <div className="hotelPricingInlineSwitch hotelPricingInlineSwitchWrap">
                <label className="hpCheckboxRow">
                  <input
                    type="checkbox"
                    checked={!!hotelPackageForm.watch('isPerNight')}
                    onChange={(e) => hotelPackageForm.setValue('isPerNight', e.target.checked)}
                  />
                  <span>{i18n.t('hotelPricing.fields.isPerNight')}</span>
                </label>

                <label className="hpCheckboxRow">
                  <input
                    type="checkbox"
                    checked={!!hotelPackageForm.watch('isPerPerson')}
                    onChange={(e) => hotelPackageForm.setValue('isPerPerson', e.target.checked)}
                  />
                  <span>{i18n.t('hotelPricing.fields.isPerPerson')}</span>
                </label>
              </div>

              <div className="hotelPricingModalFooter">
                <button type="button" className="hpSecondaryBtn" onClick={() => setHotelPackageModalOpen(false)}>
                  {i18n.t('generic.cancel')}
                </button>
                <button type="submit" className="hpPrimaryBtn">
                  {i18n.t(selectedHotelPackage ? 'hotelPricing.actions.update' : 'hotelPricing.actions.create')}
                </button>
              </div>
            </form>
          </GeneralModal>
        )}
      </div>
    </BasicCli>
  );
};

function toDateInput(value?: string) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function normalizePagination(value: any, fallback: PaginationType): PaginationType {
  return {
    page: value?.page ?? fallback.page,
    itemPerPage: value?.itemPerPage ?? fallback.itemPerPage,
    count: value?.count ?? fallback.count,
    itemsPerPage: value?.itemsPerPage ?? fallback.itemsPerPage ?? [10, 20, 50, 100],
  };
}

function isSamePagination(a: PaginationType, b: PaginationType): boolean {
  if (a.page !== b.page) return false;
  if (a.itemPerPage !== b.itemPerPage) return false;
  if (a.count !== b.count) return false;
  if (a.itemsPerPage.length !== b.itemsPerPage.length) return false;

  return a.itemsPerPage.every((value, index) => value === b.itemsPerPage[index]);
}

function buildGridRequest(gridItems: GridItem[], hotelFilter: { entity: string; value: string } | null, fallbackColumns: GridItem[]) {
  const source = Array.isArray(gridItems) && gridItems.length > 0 ? gridItems : fallbackColumns;
  const nextItems = source.map((item) => ({ ...item, filter: item.filter ? { ...item.filter } : undefined }));

  if (!hotelFilter?.value) {
    return nextItems;
  }

  const existingIndex = nextItems.findIndex((item) => item.entity.toLowerCase() === hotelFilter.entity.toLowerCase());
  const filter = {
    value: hotelFilter.value,
    operator: 'eq',
  };

  if (existingIndex >= 0) {
    nextItems[existingIndex] = {
      ...nextItems[existingIndex],
      filter,
    };
    return nextItems;
  }

  return [
    ...nextItems,
    {
      entity: hotelFilter.entity,
      label: 'HotelId',
      isShown: false,
      filter,
    },
  ];
}

export default HotelPricing;
